import {helpdeskAxios} from '@/plugins/helpdeskAxios';
import {settings} from '@/plugins/settings';

// ── Inline (cid:) images ──────────────────────────────────────────────────────
// Received emails reference their pasted images as `cid:<id>` (a part of the original MIME message).
// The helpdesk doesn't store them: the backend reads them from Gmail on demand, all the images of an
// email in one request. We keep a blob URL per cid for the page load: a signature quoted all over a
// thread is downloaded once.

export type InlineImageSource = 'received' | 'sent';

const CID_SRC_RE = /(src\s*=\s*)(["'])cid:([^"']+)\2/gi;

// Shown where an image can't be recovered (the original email was deleted from Gmail...).
export const MISSING_IMAGE_SRC = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="168" height="44" viewBox="0 0 168 44">'
  + '<rect x="0.5" y="0.5" width="167" height="43" rx="4" fill="#f7f8f9" stroke="#c1c7d0" stroke-dasharray="4 3"/>'
  + '<text x="84" y="27" font-family="Arial,sans-serif" font-size="12" fill="#626f86" text-anchor="middle">Imagen no disponible</text>'
  + '</svg>',
);
// Keeps the layout (width/height attributes) while the real image loads.
export const LOADING_IMAGE_SRC = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

// Backend limit of images per request.
const MAX_CIDS_PER_REQUEST = 30;

// Lower-cased cid -> blob URL (null = not recoverable).
const cache: {[cid: string]: Promise<string | null>} = {};

function decodeEntities(value: string): string {
  return value.replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#39;/g, '\'');
}

export function cidReferences(html: string): string[] {
  const found: string[] = [];
  (html || '').replace(CID_SRC_RE, (_m: string, _p: string, _q: string, cid: string) => {
    const id = decodeEntities(cid).trim();
    if (id && found.indexOf(id) === -1) found.push(id);
    return _m;
  });
  return found;
}

function blobUrl(image: {mime: string; data: string}): string {
  const binary = atob(image.data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], {type: image.mime}));
}

/** Blob URLs for the `cid:` images of an email (null where it can't be recovered). */
export function resolveInlineImages(
  source: InlineImageSource, emailId: number, cids: string[],
): Promise<{[cid: string]: string | null}> {
  const toFetch = cids.filter((cid) => !cache[cid.toLowerCase()]);
  for (let i = 0; i < toFetch.length; i += MAX_CIDS_PER_REQUEST) {
    const chunk = toFetch.slice(i, i + MAX_CIDS_PER_REQUEST);
    const path = (source === 'sent' ? 'v1/sent/' : 'v1/inbox/') + emailId + '/inline-images';
    const request = helpdeskAxios({url: settings.helpdeskEndpoint + path, method: 'GET', params: {cids: chunk}})
      .then((res: any) => (res.data && res.data.data && res.data.data.images) || {});
    chunk.forEach((cid) => {
      const key = cid.toLowerCase();
      cache[key] = request
        .then((images: any) => (images[cid] ? blobUrl(images[cid]) : null))
        .catch(() => {
          // Network/server error: let a later view retry.
          delete cache[key];
          return null;
        });
    });
  }

  return Promise.all(cids.map((cid) => cache[cid.toLowerCase()])).then((urls) => {
    const result: {[cid: string]: string | null} = {};
    cids.forEach((cid, i) => { result[cid] = urls[i]; });
    return result;
  });
}

/** `cid:` sources replaced by `placeholder`, keeping the cid in `data-hd-cid` to swap the real image in later. */
export function markCidImages(html: string, placeholder: string): string {
  return (html || '').replace(CID_SRC_RE, (_m: string, prefix: string, quote: string, cid: string) =>
    prefix + quote + placeholder + quote + ' data-hd-cid=' + quote + cid + quote);
}

// ── Images the recipient won't see ────────────────────────────────────────────
// Content copied from Gmail/Outlook often carries images that only work on the author's screen.

export type BrokenImageReason = 'cid' | 'gmail' | 'local' | 'unreachable';

export interface IBrokenImage {
  src: string;
  reason: BrokenImageReason;
}

export const BROKEN_IMAGE_REASONS: {[reason: string]: string} = {
  cid:         'Viene copiada de otro correo y no se puede volver a enviar.',
  gmail:       'Es un enlace privado de Gmail: solo se ve con tu sesión abierta.',
  local:       'Es un archivo temporal de tu navegador.',
  unreachable: 'No se ha podido descargar.',
};

export function brokenReasonForSrc(rawSrc: string): BrokenImageReason | null {
  const src = decodeEntities((rawSrc || '').trim()).toLowerCase();
  if (src.indexOf('cid:') === 0) return 'cid';
  if (/^https?:\/\/mail\.google\.com\//.test(src)) return 'gmail';
  if (src.indexOf('blob:') === 0 || src.indexOf('file:') === 0 || src.indexOf('webkit-fake-url:') === 0) return 'local';
  return null;
}

/** Images of `html` that won't reach the recipient. `failedSrcs`: images the browser couldn't load. */
export function findBrokenImages(html: string, failedSrcs: string[] = []): IBrokenImage[] {
  const result: IBrokenImage[] = [];
  const re = /<img\b[^>]*?\bsrc\s*=\s*(["'])([^"']*)\1/gi;
  let match: RegExpExecArray | null;
  while ((match = re.exec(html || '')) !== null) {
    const src = match[2];
    const reason = brokenReasonForSrc(src) || (failedSrcs.indexOf(decodeEntities(src)) !== -1 ? 'unreachable' : null);
    if (reason) result.push({src, reason});
  }
  return result;
}

/** The same HTML without the images `findBrokenImages` reports. */
export function removeBrokenImages(html: string, failedSrcs: string[] = []): string {
  const container = document.createElement('div');
  container.innerHTML = html || '';
  Array.from(container.querySelectorAll('img')).forEach((img) => {
    const src = img.getAttribute('src') || '';
    if (brokenReasonForSrc(src) || failedSrcs.indexOf(src) !== -1) img.parentNode!.removeChild(img);
  });
  return container.innerHTML;
}
